import React, { useState, useEffect, useCallback } from 'react';
import { Download, FileText, FileSpreadsheet, Table2, CheckSquare, Square, Calendar, Save, Trash2, History } from 'lucide-react';
import api from '../api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EXPORT_OPTIONS = [
  { key: 'sales-orders', label: 'Sales Orders', date_aware: true },
  { key: 'purchase-orders', label: 'Purchase Orders', date_aware: true },
  { key: 'products', label: 'Products', date_aware: false },
  { key: 'inventory', label: 'Inventory', date_aware: false },
  { key: 'customers', label: 'Customers', date_aware: false },
  { key: 'vendors', label: 'Vendors', date_aware: false },
  { key: 'sales-quotes', label: 'Sales Quotes', date_aware: true },
  { key: 'purchase-quotes', label: 'Purchase Quotes', date_aware: true },
  { key: 'shipments-received', label: 'Shipments Received', date_aware: true },
];

const FORMATS = [
  { key: 'pdf', label: 'PDF', icon: FileText, color: 'text-red-500', desc: 'Branded document with Webb Supply letterhead' },
  { key: 'xlsx', label: 'Excel (.xlsx)', icon: FileSpreadsheet, color: 'text-green-600', desc: 'Spreadsheet with styled headers' },
  { key: 'csv', label: 'CSV', icon: Table2, color: 'text-blue-500', desc: 'Plain data — compatible with any spreadsheet app' },
];

const DATE_RANGES = [
  { key: 'last_30', label: 'Last 30 Days' },
  { key: 'last_year', label: 'Last Year' },
  { key: 'all_time', label: 'All Time' },
  { key: 'custom', label: 'Custom Range' },
];

const fmtDateTime = (d) => d ? new Date(d).toLocaleString() : '—';
const fmtSize = (b) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(2)} MB` : `${(b / 1024).toFixed(1)} KB`;

export default function ExportPage() {
  const [selected, setSelected] = useState(new Set());
  const [format, setFormat] = useState('pdf');
  const [dateRange, setDateRange] = useState('all_time');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [saveToHistory, setSaveToHistory] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [results, setResults] = useState([]);
  const [stored, setStored] = useState([]);
  const [loadingStored, setLoadingStored] = useState(true);

  const loadStored = useCallback(async () => {
    setLoadingStored(true);
    try {
      const data = await api.request('/api/exports-history');
      setStored(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoadingStored(false); }
  }, []);

  useEffect(() => { loadStored(); }, [loadStored]);

  const toggleItem = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(selected.size === EXPORT_OPTIONS.length ? new Set() : new Set(EXPORT_OPTIONS.map(o => o.key)));
  };

  const buildExportUrl = (key) => {
    const params = new URLSearchParams();
    params.append('date_range', dateRange);
    if (dateRange === 'custom') {
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
    }
    if (saveToHistory) params.append('save', 'true');
    return `/api/tab-export/${key}/${format}?${params.toString()}`;
  };

  const handleExport = async () => {
    if (selected.size === 0) return;
    if (dateRange === 'custom' && (!fromDate || !toDate)) {
      alert('Please choose from and to dates for the custom range.');
      return;
    }
    setExporting(true);
    setResults([]);
    const newResults = [];

    for (const key of selected) {
      const label = EXPORT_OPTIONS.find(o => o.key === key)?.label || key;
      try {
        await api.downloadReportExport(buildExportUrl(key));
        newResults.push({ key, label, status: 'success' });
      } catch (err) {
        newResults.push({ key, label, status: 'error', message: err.message });
      }
    }

    setResults(newResults);
    setExporting(false);
    if (saveToHistory) loadStored();
  };

  const downloadStored = async (item) => {
    try {
      const token = localStorage.getItem('access_token');
      const resp = await fetch(`${API_URL}/api/exports-history/${item.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Download failed: ' + e.message); }
  };

  const deleteStored = async (item) => {
    if (!window.confirm(`Delete stored export "${item.filename}"?`)) return;
    try {
      await api.request(`/api/exports-history/${item.id}`, { method: 'DELETE' });
      loadStored();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const allSelected = selected.size === EXPORT_OPTIONS.length;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="export-page">
      <div>
        <h2 className="text-3xl font-semibold tracking-tight">Export Data</h2>
        <p className="text-muted-foreground mt-1">Select the data you want to export and choose a format</p>
      </div>

      {/* Date Range */}
      <div className="bg-card border border-border rounded-lg p-5">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <Calendar className="h-4 w-4" /> Date Range
        </h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border border-input rounded-md text-sm bg-background min-w-[180px]"
              data-testid="date-range-select"
            >
              {DATE_RANGES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          {dateRange === 'custom' && (
            <>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">From</label>
                <input
                  type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                  className="px-3 py-2 border border-input rounded-md text-sm bg-background"
                  data-testid="from-date-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">To</label>
                <input
                  type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                  className="px-3 py-2 border border-input rounded-md text-sm bg-background"
                  data-testid="to-date-input"
                />
              </div>
            </>
          )}
          <p className="text-xs text-muted-foreground ml-2 self-end pb-2.5">Date filter applies to time-based tabs (Sales Orders, POs, Quotes, Shipments). Static tabs (Products, Customers, Vendors, Inventory) ignore the filter.</p>
        </div>
      </div>

      {/* Format Selection */}
      <div className="bg-card border border-border rounded-lg p-5">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Export Format</h3>
        <div className="grid grid-cols-3 gap-3">
          {FORMATS.map(f => (
            <label
              key={f.key}
              className={`flex items-center gap-3 p-3.5 rounded-lg border cursor-pointer transition-colors ${
                format === f.key ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-muted/50'
              }`}
              data-testid={`format-${f.key}`}
            >
              <input type="radio" name="format" value={f.key}
                checked={format === f.key} onChange={() => setFormat(f.key)}
                className="sr-only" />
              <f.icon className={`h-5 w-5 ${f.color}`} />
              <div>
                <p className="text-sm font-medium">{f.label}</p>
                <p className="text-xs text-muted-foreground">{f.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Data Selection */}
      <div className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Select Data to Export</h3>
          <button onClick={selectAll}
            className="text-xs text-primary hover:underline font-medium"
            data-testid="select-all-btn">
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {EXPORT_OPTIONS.map(opt => (
            <label
              key={opt.key}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selected.has(opt.key) ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/30'
              }`}
              data-testid={`export-option-${opt.key}`}
            >
              <input type="checkbox" checked={selected.has(opt.key)}
                onChange={() => toggleItem(opt.key)}
                className="sr-only" />
              {selected.has(opt.key)
                ? <CheckSquare className="h-5 w-5 text-primary flex-shrink-0" />
                : <Square className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              }
              <div className="flex-1">
                <span className="text-sm font-medium">{opt.label}</span>
                {!opt.date_aware && <span className="text-[10px] uppercase ml-2 text-muted-foreground">(no dates)</span>}
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Save toggle + Export Button */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="save-toggle">
          <input
            type="checkbox" checked={saveToHistory} onChange={(e) => setSaveToHistory(e.target.checked)}
            className="rounded"
          />
          <Save className="h-4 w-4" /> Save to Stored Exports
        </label>
        <div className="flex items-center gap-4">
          <button
            onClick={handleExport}
            disabled={selected.size === 0 || exporting}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            data-testid="export-btn"
          >
            <Download className="h-5 w-5" />
            {exporting ? 'Exporting...' : `Export ${selected.size} item${selected.size !== 1 ? 's' : ''} as ${format.toUpperCase()}`}
          </button>
        </div>
      </div>
      {selected.size === 0 && (
        <p className="text-sm text-muted-foreground -mt-3">Check at least one item to export</p>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-5" data-testid="export-results">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Export Results</h3>
          <div className="space-y-2">
            {results.map(r => (
              <div key={r.key} className={`flex items-center justify-between p-2.5 rounded-md ${
                r.status === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}>
                <span className="text-sm font-medium">{r.label}</span>
                <span className="text-xs">
                  {r.status === 'success' ? 'Downloaded' : `Failed: ${r.message}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stored Exports */}
      <div className="bg-card border border-border rounded-lg p-5" data-testid="stored-exports">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <History className="h-4 w-4" /> Stored Exports
        </h3>
        {loadingStored ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : stored.length === 0 ? (
          <p className="text-sm text-muted-foreground">No stored exports yet. Toggle "Save to Stored Exports" before exporting to keep a copy here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground text-left">
                  <th className="pb-2 font-medium">Filename</th>
                  <th className="pb-2 font-medium">Range</th>
                  <th className="pb-2 font-medium text-right">Rows</th>
                  <th className="pb-2 font-medium text-right">Size</th>
                  <th className="pb-2 font-medium">Created</th>
                  <th className="pb-2 font-medium">By</th>
                  <th className="pb-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {stored.map(item => (
                  <tr key={item.id} className="border-b border-border/50" data-testid={`stored-row-${item.id}`}>
                    <td className="py-2 font-medium truncate max-w-[260px]">{item.filename}</td>
                    <td className="py-2 text-xs text-muted-foreground">
                      {item.date_range === 'custom' ? `${item.from_date || '?'} → ${item.to_date || '?'}` : (item.date_range || 'all_time').replace('_', ' ')}
                    </td>
                    <td className="py-2 text-right">{item.row_count}</td>
                    <td className="py-2 text-right text-xs">{fmtSize(item.size_bytes)}</td>
                    <td className="py-2 text-xs">{fmtDateTime(item.created_at)}</td>
                    <td className="py-2 text-xs text-muted-foreground">{item.created_by}</td>
                    <td className="py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => downloadStored(item)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Download"
                          data-testid={`download-stored-${item.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteStored(item)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                          data-testid={`delete-stored-${item.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
